<script setup lang="ts" generic="T extends string">
import UIKit from '@pocketprep/ui-kit'
import { onBeforeUnmount, onMounted } from 'vue'
const {
    Link: PocketLink,
    Button: PocketButton,
    Icon,
} = UIKit

const props = defineProps<{
    actions: T[]
    instanceId: string
    isActive: boolean
    disableCopyLink?: boolean
}>()

const emit = defineEmits<{
    'action': [action: T, instanceId: string]
    'toggle': [instanceId: string]
}>()

const emitAction = (action: T) => {
    emit('action', action, props.instanceId)
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        emit('toggle', props.instanceId)
    }
}

const clickListener = (e: MouseEvent) => {
    if (props.isActive) {
        const targetEl = e.target as HTMLElement
        if (!targetEl?.closest('.actions')
            && props.isActive && document.body.contains(targetEl)
        ) {
            emit('toggle', props.instanceId)
        }
    }
}

onMounted(() => {
    document.addEventListener('click', clickListener)
    document.addEventListener('keydown', keydownListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
    document.removeEventListener('keydown', keydownListener)
})
</script>

<template>
    <div class="actions">
        <PocketLink
            :has-icon="true"
            :aria-expanded="props.isActive ? 'true' : 'false'"
            class="actions__link"
            role="button"
            type="tertiary"
            tabindex="0"
            @keypress.enter="emit('toggle', props.instanceId)"
            @click.stop="emit('toggle', props.instanceId)"
        >
            <Icon type="quickActions" />
        </PocketLink>
        <div class="actions__dropdown" v-if="props.isActive">
            <div v-for="action in props.actions" :key="action">
                <PocketButton
                    @click.stop="emitAction(action)"
                    :disabled="action === 'Copy Link' && props.disableCopyLink"
                    type="secondary"
                >
                    {{ action }}
                </PocketButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.actions {
    position: relative;

    &__link {

        &:hover {
            color: color.mix($brand-blue, $brand-black, 10%)
        }

        svg {
            height: 24px;
            width: 24px;
        }
    }

    &__dropdown {
        background: $white;
        border-radius: 8px;
        box-shadow: 0 6px 22px 0 rgba($brand-black, 0.1);
        padding: 12px;
        box-sizing: border-box;
        width: 136px;
        font-weight: 600;
        max-height: 180px;
        overflow: auto;
        position: absolute;
        right: -36px;
        z-index: 5;
    }
}
</style>
